import {
  PLAYBOOK_CONDITION_FIELDS,
  PLAYBOOK_CONDITION_OPERATORS,
  PLAYBOOK_STAGE_LABELS,
  PLAYBOOK_STAGES,
  PLAYBOOK_VERSION_STATUS_LABELS,
  QUALIFICATION_FIELD_LABELS,
  type PlaybookCondition,
  type PlaybookStage,
  type PlaybookStep,
  type PlaybookVersionStatus,
} from '@lia/contracts';
import { ArrowDown01Icon, ArrowUp01Icon, Delete02Icon } from '@hugeicons/core-free-icons';
import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { formatDateTime } from '../../lib/format.js';
import { StringListEditor } from '../patterns/string-list-editor.js';
import { StatusBadge } from '../primitives/badge.js';
import { Button, IconButton } from '../primitives/button.js';
import { Checkbox } from '../primitives/checkbox.js';
import { FormField } from '../primitives/form-field.js';
import { Icon } from '../primitives/icon.js';
import { Input, Textarea } from '../primitives/input.js';
import { Select } from '../primitives/select.js';
import { PLAYBOOK_VERSION_STATUS_TONE } from './copilot-status.js';

// Copilot playbook editor building blocks (addendum §5). Presentational: data in by props, changes out by callbacks.
// Conditions are controlled field/operator/value triples (never executable code).

type ConditionField = (typeof PLAYBOOK_CONDITION_FIELDS)[number];
type ConditionOperator = (typeof PLAYBOOK_CONDITION_OPERATORS)[number];

export const PLAYBOOK_CONDITION_FIELD_LABELS: Record<ConditionField, string> = {
  call_direction: 'Dirección de la llamada',
  project_selected: 'Proyecto elegido',
  ...QUALIFICATION_FIELD_LABELS,
};

export const PLAYBOOK_CONDITION_OPERATOR_LABELS: Record<ConditionOperator, string> = {
  equals: 'es igual a',
  not_equals: 'es distinto de',
  is_known: 'ya se conoce',
  is_unknown: 'aún no se conoce',
};

const DIRECTION_OPTIONS = [
  { value: 'inbound', label: 'Entrante' },
  { value: 'outbound', label: 'Saliente' },
];

/** Operators that make sense for a field (project_selected is a yes/no fact). */
export function conditionOperatorsFor(field: ConditionField): ConditionOperator[] {
  return field === 'project_selected' ? ['is_known', 'is_unknown'] : [...PLAYBOOK_CONDITION_OPERATORS];
}

const needsValue = (op: ConditionOperator) => op === 'equals' || op === 'not_equals';

export interface PlaybookConditionEditorProps {
  value: PlaybookCondition | null;
  onChange: (value: PlaybookCondition | null) => void;
  disabled?: boolean;
  /** Prefix for accessible names, e.g. «Paso 2». */
  label?: string;
  error?: React.ReactNode;
}

/** «Aplica solo si…» condition: controlled field + operator (+ value when the operator compares). */
export function PlaybookConditionEditor({ value, onChange, disabled, label = 'Paso', error }: PlaybookConditionEditorProps) {
  const id = React.useId();
  if (!value) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-body text-text-secondary">Siempre aplica.</span>
        {!disabled ? (
          <Button size="sm" variant="secondary" onClick={() => onChange({ field: 'call_direction', operator: 'equals', value: 'inbound' })}>
            Agregar condición
          </Button>
        ) : null}
      </div>
    );
  }
  const ops = conditionOperatorsFor(value.field);
  const setField = (field: string) => {
    const f = field as ConditionField;
    const operator = conditionOperatorsFor(f).includes(value.operator) ? value.operator : 'is_known';
    onChange(needsValue(operator) ? { field: f, operator, value: f === 'call_direction' ? 'inbound' : '' } : { field: f, operator });
  };
  const setOperator = (op: string) => {
    const operator = op as ConditionOperator;
    onChange(needsValue(operator) ? { field: value.field, operator, value: value.value ?? (value.field === 'call_direction' ? 'inbound' : '') } : { field: value.field, operator });
  };
  return (
    <div role="group" aria-label={`${label}: condición`} className="grid gap-2">
      <div className="grid gap-2 md:grid-cols-[1fr_1fr_1fr_auto] md:items-end">
        <FormField label="Aplica solo si" id={`${id}-field`}>
          <Select
            value={value.field}
            onValueChange={setField}
            disabled={disabled}
            options={PLAYBOOK_CONDITION_FIELDS.map((f) => ({ value: f, label: PLAYBOOK_CONDITION_FIELD_LABELS[f] }))}
          />
        </FormField>
        <FormField label="Operador" id={`${id}-op`}>
          <Select value={value.operator} onValueChange={setOperator} disabled={disabled} options={ops.map((o) => ({ value: o, label: PLAYBOOK_CONDITION_OPERATOR_LABELS[o] }))} />
        </FormField>
        {needsValue(value.operator) ? (
          <FormField label="Valor" id={`${id}-value`} required>
            {value.field === 'call_direction' ? (
              <Select value={value.value || undefined} onValueChange={(v) => onChange({ ...value, value: v })} disabled={disabled} options={DIRECTION_OPTIONS} />
            ) : (
              <Input value={value.value ?? ''} maxLength={120} disabled={disabled} onChange={(e) => onChange({ ...value, value: e.target.value })} />
            )}
          </FormField>
        ) : (
          <span aria-hidden />
        )}
        {!disabled ? (
          <Button size="sm" variant="ghost" onClick={() => onChange(null)}>
            Quitar condición
          </Button>
        ) : null}
      </div>
      {error ? <p className="text-meta font-medium text-danger">{error}</p> : null}
    </div>
  );
}

export interface PlaybookStepEditorProps {
  step: PlaybookStep;
  /** Zero-based position. */
  index: number;
  total: number;
  onChange: (patch: Partial<Omit<PlaybookStep, 'condition'>>) => void;
  onConditionChange: (condition: PlaybookCondition | null) => void;
  onMove: (delta: -1 | 1) => void;
  onRemove: () => void;
  readOnly?: boolean;
  className?: string;
}

/** Form for one playbook step: stable key, stage, title, instructions, suggested question, required, condition, evidence. */
export function PlaybookStepEditor({ step, index, total, onChange, onConditionChange, onMove, onRemove, readOnly, className }: PlaybookStepEditorProps) {
  const id = React.useId();
  const name = `Paso ${index + 1}`;
  return (
    <fieldset className={cn('grid gap-3 rounded-card border border-border-subtle bg-surface-default p-4', className)} aria-label={`${name}${step.title ? `: ${step.title}` : ''}`}>
      <legend className="sr-only">{name}</legend>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-body font-semibold text-text-primary">
          {name}
          {step.title ? ` · ${step.title}` : ''}
        </span>
        {step.required ? <StatusBadge tone="info">Obligatorio</StatusBadge> : <StatusBadge tone="neutral">Opcional</StatusBadge>}
        {!readOnly ? (
          <span className="ml-auto flex gap-1">
            <IconButton size="sm" label={`Subir ${name}`} icon={<Icon icon={ArrowUp01Icon} aria-hidden />} disabled={index === 0} onClick={() => onMove(-1)} />
            <IconButton size="sm" label={`Bajar ${name}`} icon={<Icon icon={ArrowDown01Icon} aria-hidden />} disabled={index === total - 1} onClick={() => onMove(1)} />
            <IconButton size="sm" label={`Quitar ${name}`} icon={<Icon icon={Delete02Icon} aria-hidden />} onClick={onRemove} />
          </span>
        ) : null}
      </div>
      <div className="grid gap-3 md:grid-cols-3">
        <FormField label="Título" required id={`${id}-title`}>
          <Input value={step.title} maxLength={120} disabled={readOnly} onChange={(e) => onChange({ title: e.target.value })} />
        </FormField>
        <FormField label="Etapa" id={`${id}-stage`}>
          <Select
            value={step.stage}
            onValueChange={(v) => onChange({ stage: v as PlaybookStage })}
            disabled={readOnly}
            options={PLAYBOOK_STAGES.map((s) => ({ value: s, label: PLAYBOOK_STAGE_LABELS[s] }))}
          />
        </FormField>
        <FormField label="Clave" description="Estable entre versiones: minúsculas, números y guion bajo." id={`${id}-key`}>
          <Input value={step.key} maxLength={40} disabled={readOnly} onChange={(e) => onChange({ key: e.target.value })} className="font-mono" />
        </FormField>
      </div>
      <FormField label="Instrucciones para el asesor" id={`${id}-instructions`}>
        <Textarea rows={2} value={step.instructions} maxLength={2000} disabled={readOnly} onChange={(e) => onChange({ instructions: e.target.value })} />
      </FormField>
      <FormField label="Pregunta sugerida" id={`${id}-question`}>
        <Input value={step.suggestedQuestion ?? ''} maxLength={500} disabled={readOnly} onChange={(e) => onChange({ suggestedQuestion: e.target.value || null })} />
      </FormField>
      <Checkbox
        label="Paso obligatorio"
        description="Se cubre solo con evidencia en la transcripción o con la confirmación del asesor."
        checked={step.required}
        disabled={readOnly}
        onCheckedChange={(v) => onChange({ required: v === true })}
      />
      <PlaybookConditionEditor value={step.condition} onChange={onConditionChange} disabled={readOnly} label={name} />
      <div className="grid gap-1.5">
        <span className="text-body font-medium text-text-primary">Frases de evidencia</span>
        <p className="text-meta text-text-secondary">Si se escuchan en la llamada (sin importar acentos ni mayúsculas), cuentan como evidencia del paso.</p>
        <StringListEditor
          label={`Frases de evidencia del ${name}`}
          value={step.evidenceCriteria}
          onChange={(v) => onChange({ evidenceCriteria: v })}
          max={20}
          maxLength={200}
          addLabel="Agregar frase"
          disabled={readOnly}
        />
      </div>
    </fieldset>
  );
}

export interface PlaybookVersionItem {
  id: string;
  version: number;
  status: PlaybookVersionStatus;
  changeNote: string | null;
  createdAt: string;
  testedAt: string | null;
  publishedAt: string | null;
  archivedAt: string | null;
  stepCount: number;
}

export interface PlaybookVersionAction {
  action: string;
  label: string;
}

export interface PlaybookVersionListProps {
  versions: readonly PlaybookVersionItem[];
  timezone: string;
  /** Valid actions for a version (the caller applies status + role rules). */
  actionsFor: (v: PlaybookVersionItem) => readonly PlaybookVersionAction[];
  onAction: (v: PlaybookVersionItem, action: string) => void;
  /** `${versionId}:${action}` while a request is running. */
  pending?: string | null;
  className?: string;
}

/** Versions newest first with their state (Borrador → Probado → Publicado → Archivado) and only valid actions. */
export function PlaybookVersionList({ versions, timezone, actionsFor, onAction, pending, className }: PlaybookVersionListProps) {
  const sorted = [...versions].sort((a, b) => b.version - a.version);
  if (!sorted.length) return <p className={cn('text-body text-text-secondary', className)}>Este playbook aún no tiene versiones.</p>;
  return (
    <ol aria-label="Versiones del playbook" className={cn('grid gap-2', className)}>
      {sorted.map((v) => {
        const actions = actionsFor(v);
        const dates = [
          `Creada ${formatDateTime(v.createdAt, timezone)}`,
          v.testedAt ? `probada ${formatDateTime(v.testedAt, timezone)}` : null,
          v.publishedAt ? `publicada ${formatDateTime(v.publishedAt, timezone)}` : null,
          v.archivedAt ? `archivada ${formatDateTime(v.archivedAt, timezone)}` : null,
        ].filter(Boolean);
        return (
          <li key={v.id} className="grid gap-2 rounded-card border border-border-subtle bg-surface-default p-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-body font-semibold text-text-primary">Versión {v.version}</span>
              <StatusBadge tone={PLAYBOOK_VERSION_STATUS_TONE[v.status]}>{PLAYBOOK_VERSION_STATUS_LABELS[v.status]}</StatusBadge>
              <span className="text-meta text-text-secondary">{v.stepCount === 1 ? '1 paso' : `${v.stepCount} pasos`}</span>
              {actions.length ? (
                <span className="ml-auto flex flex-wrap gap-2">
                  {actions.map((a) => (
                    <Button
                      key={a.action}
                      size="sm"
                      variant={a.action === 'publish' ? 'primary' : 'secondary'}
                      loading={pending === `${v.id}:${a.action}`}
                      disabled={!!pending && pending !== `${v.id}:${a.action}`}
                      aria-label={`${a.label} (versión ${v.version})`}
                      onClick={() => onAction(v, a.action)}
                    >
                      {a.label}
                    </Button>
                  ))}
                </span>
              ) : null}
            </div>
            <p className="ef-tabular text-meta text-text-secondary">{dates.join(' · ')}</p>
            {v.changeNote ? <p className="text-body text-text-primary">{v.changeNote}</p> : null}
          </li>
        );
      })}
    </ol>
  );
}

export interface PlaybookStepSummaryListProps {
  steps: readonly Pick<PlaybookStep, 'key' | 'stage' | 'title' | 'suggestedQuestion' | 'required'>[];
  label?: string;
  className?: string;
}

/** Compact read-only list of steps (import preview, published content). */
export function PlaybookStepSummaryList({ steps, label = 'Pasos', className }: PlaybookStepSummaryListProps) {
  if (!steps.length) return <p className={cn('text-body text-text-secondary', className)}>Sin pasos.</p>;
  return (
    <ol aria-label={label} className={cn('grid gap-1.5', className)}>
      {steps.map((s, i) => (
        <li key={s.key} className="grid gap-0.5 rounded-control bg-surface-subtle px-3 py-2">
          <span className="text-body text-text-primary">
            {i + 1}. {s.title || <span className="text-text-secondary">(sin título)</span>}
          </span>
          <span className="text-meta text-text-secondary">
            {PLAYBOOK_STAGE_LABELS[s.stage]} · {s.required ? 'Obligatorio' : 'Opcional'} · <span className="font-mono">{s.key}</span>
            {s.suggestedQuestion ? ` · Pregunta: ${s.suggestedQuestion}` : ''}
          </span>
        </li>
      ))}
    </ol>
  );
}
